import { useEffect, useMemo, useState } from 'react';
import { Alert, Pressable, SafeAreaView, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import * as Linking from 'expo-linking';
import { Link, router, useLocalSearchParams } from 'expo-router';
import { Image } from 'expo-image';

import { AuthButton, AuthInput, AuthLinkRow, AuthNote, AuthScreen } from '@/components/auth/auth-ui';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/auth-provider';
import slideOneSource from '../../assets/auth-slider/slide-1.webp';
import slideThreeSource from '../../assets/auth-slider/slide-3.webp';
import slideTwoSource from '../../assets/auth-slider/slide-2.webp';
import logoSource from '../../assets/images/mcqgen-logo-trimmed.png';

const desktopSlides = [
  slideOneSource,
  slideTwoSource,
  slideThreeSource,
];

type RecoveryStage = 'request' | 'verify' | 'update';

const recoverySteps: { key: RecoveryStage; label: string }[] = [
  { key: 'request', label: 'Email' },
  { key: 'verify', label: 'OTP' },
  { key: 'update', label: 'Password' },
];

export default function ForgotPasswordScreen() {
  const params = useLocalSearchParams<{ email?: string }>();
  const { authFlow, isConfigured, setAuthFlow } = useAuth();
  const [email, setEmail] = useState(typeof params.email === 'string' ? params.email : '');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [stage, setStage] = useState<RecoveryStage>('request');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusTone, setStatusTone] = useState<'neutral' | 'warn'>('neutral');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [desktopSlide, setDesktopSlide] = useState(0);
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const redirectTo = useMemo(() => Linking.createURL('/reset-password'), []);
  const normalizedOtp = otp.replace(/\s+/g, '');

  useEffect(() => {
    if (!isDesktop) {
      return;
    }

    const timer = setInterval(() => {
      setDesktopSlide((current) => (current + 1) % desktopSlides.length);
    }, 3200);

    return () => clearInterval(timer);
  }, [isDesktop]);

  useEffect(() => {
    if (authFlow === 'recovery') {
      setStage('update');
    }
  }, [authFlow]);

  useEffect(() => {
    if (typeof params.email === 'string' && params.email) {
      setEmail(params.email);
    }
  }, [params.email]);

  const validatePasswordFields = () => {
    if (!password || !confirmPassword) {
      Alert.alert('Missing password', 'Enter and confirm the new password.');
      return false;
    }

    if (password.length < 8) {
      Alert.alert('Weak password', 'Use at least 8 characters for the new password.');
      return false;
    }

    if (password !== confirmPassword) {
      Alert.alert('Password mismatch', 'The two password fields must match.');
      return false;
    }

    return true;
  };

  const ensureConfigured = () => {
    if (!isConfigured) {
      Alert.alert('Supabase not configured', 'Add your Supabase environment variables first.');
      return false;
    }

    return true;
  };

  const handleSendCode = async () => {
    if (!email) {
      Alert.alert('Missing email', 'Enter the email address for the account.');
      return;
    }

    if (!ensureConfigured()) {
      return;
    }

    setSendingCode(true);
    setStatusMessage('');
    setStatusTone('neutral');
    setStage('verify');
    setOtp('');
    setPassword('');
    setConfirmPassword('');
    setStatusMessage(`Sending recovery code to ${email.trim()}...`);

    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo,
    });

    setSendingCode(false);

    if (error) {
      setStatusTone('warn');
      setStatusMessage(`Could not send recovery code: ${error.message}`);
      return;
    }

    setStatusTone('neutral');
    setStatusMessage(`Recovery code sent to ${email.trim()}. Open your email and enter the code.`);
  };

  const handleVerifyCode = async () => {
    if (!email) {
      Alert.alert('Missing email', 'Enter the email address used for the recovery request.');
      return;
    }

    if (!normalizedOtp || normalizedOtp.length < 6) {
      Alert.alert('Invalid code', 'Enter the recovery code from the email.');
      return;
    }

    if (!ensureConfigured()) {
      return;
    }

    setVerifyingCode(true);
    setStatusMessage('');
    setStatusTone('neutral');

    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: normalizedOtp,
      type: 'recovery',
    });

    setVerifyingCode(false);

    if (error) {
      setStatusTone('warn');
      setStatusMessage(`Verification failed: ${error.message}`);
      return;
    }

    setAuthFlow('recovery');
    setStage('update');
    setStatusTone('neutral');
    setStatusMessage('Code verified. Set your new password below.');
  };

  const handleUpdatePassword = async () => {
    if (!ensureConfigured()) {
      return;
    }

    if (!validatePasswordFields()) {
      return;
    }

    setUpdatingPassword(true);
    setStatusMessage('');
    setStatusTone('neutral');

    const { error } = await supabase.auth.updateUser({
      password,
    });

    setUpdatingPassword(false);

    if (error) {
      Alert.alert('Update failed', error.message);
      return;
    }

    setAuthFlow('idle');
    await supabase.auth.signOut();
    Alert.alert('Password updated', 'Sign in again with your new password.');
    router.replace('/sign-in');
  };

  const stepIndex = recoverySteps.findIndex((item) => item.key === stage);

  const stageCopy = {
    request: {
      note: 'Step 1 of 3. Enter the account email and send the recovery code.',
      subtitle: 'Enter your school email, then verify the OTP and set a new password.',
      title: 'Recover access.',
    },
    verify: {
      note: 'Step 2 of 3. Open your email and enter the recovery code.',
      subtitle: 'Enter the code from your email to unlock password reset.',
      title: 'Verify recovery code.',
    },
    update: {
      note:
        authFlow === 'recovery'
          ? 'Step 3 of 3. Recovery session is active. Set the new password now.'
          : 'Step 3 of 3. Choose a new password and confirm it.',
      subtitle: 'Create a new password for this account.',
      title: 'Set a new password.',
    },
  }[stage];

  const formContent = (
    <>
      <View style={styles.stepRail}>
        {recoverySteps.map((item, index) => {
          const isActive = item.key === stage;
          const isComplete = index < stepIndex;
          const isLast = index === recoverySteps.length - 1;

          return (
            <View key={item.key} style={styles.stepItem}>
              <View style={styles.stepTopRow}>
                <View
                  style={[
                    styles.stepCircle,
                    isActive && styles.stepCircleActive,
                    isComplete && styles.stepCircleComplete,
                  ]}>
                  <Text
                    style={[
                      styles.stepCircleText,
                      (isActive || isComplete) && styles.stepCircleTextActive,
                    ]}>
                    {index + 1}
                  </Text>
                </View>
                {!isLast ? (
                  <View
                    style={[
                      styles.stepConnector,
                      (isActive || isComplete) && styles.stepConnectorActive,
                    ]}
                  />
                ) : null}
              </View>
              <Text
                style={[
                  styles.stepLabel,
                  isActive && styles.stepLabelActive,
                  isComplete && styles.stepLabelComplete,
                ]}>
                {item.label}
              </Text>
            </View>
          );
        })}
      </View>

      <AuthNote tone={authFlow === 'recovery' && stage === 'update' ? 'neutral' : 'neutral'}>
        {stageCopy.note}
      </AuthNote>

      {statusMessage ? <AuthNote tone={statusTone}>{statusMessage}</AuthNote> : null}

      {(stage === 'request' || stage === 'verify') ? (
        <AuthInput
          autoCapitalize="none"
          autoComplete="email"
          keyboardType="email-address"
          label="School email"
          onChangeText={setEmail}
          placeholder="admin@school.com"
          value={email}
        />
      ) : null}

      {stage === 'request' ? (
        <AuthButton loading={sendingCode} onPress={handleSendCode} title="Send code" />
      ) : null}

      {stage === 'verify' ? (
        <>
          <AuthInput
            autoCapitalize="none"
            keyboardType="number-pad"
            label="Recovery code"
            maxLength={8}
            onChangeText={setOtp}
            placeholder="Enter recovery code"
            value={otp}
          />

          <AuthButton loading={verifyingCode} onPress={handleVerifyCode} title="Verify code" />
          <AuthButton loading={sendingCode} onPress={handleSendCode} title="Resend code" variant="secondary" />
          <AuthButton
            onPress={() => {
              setStage('request');
              setOtp('');
            }}
            title="Change email"
            variant="secondary"
          />
        </>
      ) : null}

      {stage === 'update' ? (
        <>
          <AuthInput
            autoCapitalize="none"
            autoComplete="new-password"
            label="New password"
            onChangeText={setPassword}
            placeholder="At least 8 characters"
            secureTextEntry
            value={password}
          />
          <AuthInput
            autoCapitalize="none"
            autoComplete="new-password"
            label="Confirm new password"
            onChangeText={setConfirmPassword}
            placeholder="Repeat the password"
            secureTextEntry
            value={confirmPassword}
          />
          <AuthButton loading={updatingPassword} onPress={handleUpdatePassword} title="Update password" />
        </>
      ) : null}
    </>
  );

  if (!isDesktop) {
    return (
      <AuthScreen
        eyebrow="Password help"
        title={stageCopy.title}
        subtitle={stageCopy.subtitle}>
        {formContent}
        <AuthLinkRow actionHref="/sign-in" actionLabel="Back to sign in" prompt="Remembered it?" />
      </AuthScreen>
    );
  }

  return (
    <SafeAreaView style={styles.desktopSafeArea}>
      <View style={styles.desktopBg}>
        <View style={styles.desktopGlowOrbOne} />
        <View style={styles.desktopGlowOrbTwo} />
        <ScrollView contentContainerStyle={styles.desktopScroll} showsVerticalScrollIndicator={false}>
          <View style={styles.desktopShell}>
            <View style={styles.desktopFrame}>
              <View style={styles.desktopBrandPanel}>
                <View style={styles.desktopSliderStage}>
                  <View style={styles.desktopSliderImageWrap}>
                    <Image
                      contentFit="cover"
                      source={desktopSlides[desktopSlide]}
                      style={styles.desktopSliderImage}
                    />
                  </View>
                  <View style={styles.desktopSliderDots}>
                    {desktopSlides.map((_, index) => (
                      <Pressable
                        key={index}
                        onPress={() => setDesktopSlide(index)}
                        style={[
                          styles.desktopSliderDot,
                          index === desktopSlide && styles.desktopSliderDotActive,
                        ]}
                      />
                    ))}
                  </View>
                </View>
              </View>

              <View style={styles.desktopFormPanel}>
                <View style={styles.desktopCard}>
                  <View style={styles.desktopCardHeader}>
                    <View style={styles.desktopBrandRow}>
                      <View style={styles.desktopBrandMark}>
                        <Image contentFit="contain" source={logoSource} style={styles.desktopLogo} />
                      </View>
                      <View style={styles.desktopBrandCopy}>
                        <Text style={styles.desktopBrandKicker}>MCQGen</Text>
                        <Text style={styles.desktopBrandSub}>Assessment operations</Text>
                      </View>
                    </View>

                    <View style={styles.desktopHeadlineBlock}>
                      <Text style={styles.desktopEyebrow}>Password help</Text>
                      <Text style={styles.desktopTitle}>{stageCopy.title}</Text>
                      <Text style={styles.desktopSubtitle}>{stageCopy.subtitle}</Text>
                    </View>
                  </View>

                  {formContent}

                  <View style={styles.desktopFooterRow}>
                    <Text style={styles.desktopFooterPrompt}>Remembered it?</Text>
                    <Link href="/sign-in" style={styles.desktopFooterAction}>
                      Back to sign in
                    </Link>
                  </View>

                  <Link href="/" style={styles.desktopFooterBack}>
                    Back to welcome
                  </Link>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  desktopSafeArea: {
    flex: 1,
    backgroundColor: '#F5F1E8',
  },
  desktopBg: {
    flex: 1,
    backgroundColor: '#F5F1E8',
  },
  desktopGlowOrbOne: {
    position: 'absolute',
    width: 460,
    height: 460,
    borderRadius: 230,
    top: -150,
    left: -130,
    backgroundColor: 'rgba(202, 221, 204, 0.9)',
  },
  desktopGlowOrbTwo: {
    position: 'absolute',
    width: 520,
    height: 520,
    borderRadius: 260,
    right: -170,
    bottom: -170,
    backgroundColor: 'rgba(229, 217, 193, 0.78)',
  },
  desktopScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingVertical: 24,
  },
  desktopShell: {
    maxWidth: 1240,
    width: '100%',
    alignSelf: 'center',
  },
  desktopFrame: {
    width: '100%',
    minHeight: 640,
    flexDirection: 'row',
    borderRadius: 34,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D9E3DA',
    shadowColor: '#1E2C22',
    shadowOffset: { width: 0, height: 24 },
    shadowOpacity: 0.16,
    shadowRadius: 38,
    elevation: 10,
  },
  desktopBrandPanel: {
    flex: 1.08,
    backgroundColor: '#173A2A',
    paddingHorizontal: 34,
    paddingVertical: 34,
    justifyContent: 'center',
  },
  desktopBrandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  desktopBrandMark: {
    width: 68,
    height: 68,
    borderRadius: 20,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D9E3DA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopLogo: {
    width: 52,
    height: 52,
  },
  desktopBrandCopy: {
    gap: 3,
  },
  desktopBrandKicker: {
    color: '#173A2A',
    fontSize: 22,
    fontWeight: '900',
    letterSpacing: 0,
  },
  desktopBrandSub: {
    color: '#6B7D71',
    fontSize: 13,
    fontWeight: '600',
  },
  desktopHeadlineBlock: {
    gap: 4,
  },
  desktopEyebrow: {
    color: '#7A8C63',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  desktopTitle: {
    color: '#173A2A',
    fontSize: 28,
    lineHeight: 34,
    fontWeight: '900',
    letterSpacing: 0,
  },
  desktopSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    color: '#5F7166',
  },
  desktopFormPanel: {
    flex: 0.92,
    backgroundColor: '#F6F1E8',
    paddingHorizontal: 34,
    paddingVertical: 34,
    borderLeftWidth: 1,
    borderLeftColor: '#D9E3DA',
  },
  desktopCard: {
    width: '100%',
    height: '100%',
    gap: 18,
    justifyContent: 'center',
  },
  desktopCardHeader: {
    gap: 16,
    marginBottom: 8,
  },
  desktopFooterRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  desktopFooterPrompt: {
    color: '#5F7166',
    fontSize: 13,
    fontWeight: '600',
  },
  desktopFooterAction: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
    textDecorationLine: 'underline',
  },
  desktopFooterBack: {
    alignSelf: 'flex-start',
    color: '#5F7166',
    fontSize: 13,
    fontWeight: '700',
    marginTop: 'auto',
  },
  desktopSliderStage: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  desktopSliderImageWrap: {
    flex: 1,
    width: '100%',
    alignSelf: 'stretch',
    aspectRatio: 0.75,
    borderRadius: 0,
    backgroundColor: 'transparent',
    borderWidth: 0,
    overflow: 'hidden',
  },
  desktopSliderImage: {
    width: '100%',
    height: '100%',
  },
  desktopSliderDots: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  desktopSliderDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    backgroundColor: 'rgba(247,243,235,0.36)',
  },
  desktopSliderDotActive: {
    width: 28,
    backgroundColor: '#F7F3EB',
  },
  stepRail: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  stepItem: {
    flex: 1,
  },
  stepTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 30,
    height: 30,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#C7D6CA',
    backgroundColor: '#F0F4EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepCircleActive: {
    backgroundColor: '#163828',
    borderColor: '#163828',
  },
  stepCircleComplete: {
    backgroundColor: '#E7F0E8',
    borderColor: '#BFD1C2',
  },
  stepCircleText: {
    color: '#5F7166',
    fontSize: 13,
    fontWeight: '800',
  },
  stepCircleTextActive: {
    color: '#FFFFFF',
  },
  stepConnector: {
    flex: 1,
    height: 1,
    marginHorizontal: 8,
    backgroundColor: '#C7D6CA',
  },
  stepConnectorActive: {
    backgroundColor: '#8DA897',
  },
  stepLabel: {
    marginTop: 6,
    color: '#5F7166',
    fontSize: 11,
    fontWeight: '700',
    paddingRight: 6,
  },
  stepLabelActive: {
    color: '#163828',
  },
  stepLabelComplete: {
    color: '#274737',
  },
});
